import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatBytes, formatNumber, formatTradingDay, formatRecordTimeRange } from './dailyDataUtils'
import GamingVenueCheckPanel from './GamingVenueCheckPanel'

export default function BlobFilePreviewModal({ open, blobPath, feedId = 'patron', onClose }) {
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const isPatron = feedId === 'patron'
  const isMeter = feedId === 'meter'
  const isEgm = feedId === 'egm'
  const isSessions = feedId === 'sessions'
  const isSlotAnalysis = feedId === 'slot-analysis'

  const isGamingFeed = isMeter || isEgm || isSessions || isSlotAnalysis

  const uniqueCountLabel = isMeter
    ? 'Unique EGMs (sample)'
    : isEgm
      ? 'Unique machines (sample)'
      : isSlotAnalysis
        ? 'Unique EGMs (sample)'
        : isSessions
          ? 'Unique patrons (sample)'
          : 'Unique patron IDs (sample)'

  const uniqueCountValue = (isMeter || isEgm || isSlotAnalysis)
    ? (preview?.unique_egm_ids_in_sample ?? '—')
    : (preview?.unique_patron_ids_in_sample ?? '—')

  useEffect(() => {
    if (!open || !blobPath) {
      setPreview(null)
      setError(null)
      return undefined
    }

    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      setPreview(null)
      try {
        const params = new URLSearchParams({ blob_path: blobPath, lines: '15' })
        const res = await authFetch(getLocalApiUrl(`/api/daily-data/${feedId}/sources/file-preview?${params}`))
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Failed to load preview')
        if (!cancelled) setPreview(json.data)
      } catch (e) {
        if (!cancelled) setError(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [open, blobPath, feedId])

  if (!open) return null

  const keyColLabel = isEgm ? 'House #' : isMeter ? 'EGM ID' : isSlotAnalysis ? 'Date' : 'Patron'
  const secondaryColLabel = isEgm ? 'EGM PK' : isMeter ? 'Snapshot' : isSlotAnalysis ? 'EGM ID' : isSessions ? 'Start' : 'EGM'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">File preview</h2>
            <p className="text-xs font-mono text-gray-500 truncate max-w-xl" title={blobPath}>{preview?.name || blobPath}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
              <span className="ml-3 text-sm text-gray-600">Loading from Azure…</span>
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">{error}</div>
          )}

          {preview && !loading && (
            <>
              <dl className={`grid grid-cols-2 gap-3 text-sm ${isGamingFeed ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
                <div>
                  <dt className="text-gray-500 text-xs">Size</dt>
                  <dd className="font-medium">{formatBytes(preview.size)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 text-xs">Trading day</dt>
                  <dd className="font-medium">{preview.trading_day ? formatTradingDay(preview.trading_day) : '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 text-xs">Est. rows</dt>
                  <dd className="font-medium">{preview.estimated_total_rows != null ? `~${formatNumber(preview.estimated_total_rows)}` : '—'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 text-xs">{uniqueCountLabel}</dt>
                  <dd className="font-medium">{uniqueCountValue}</dd>
                </div>
                {isGamingFeed && (
                  <div>
                    <dt className="text-gray-500 text-xs">Sites in sample</dt>
                    <dd className="font-medium">{preview.unique_venue_names_in_sample ?? preview.sample_venue_names?.length ?? '—'}</dd>
                  </div>
                )}
              </dl>

              {isGamingFeed && (
                <div className="text-sm">
                  <span className="text-gray-500 text-xs uppercase tracking-wide">Record time coverage</span>
                  <p className="font-medium text-gray-900 mt-0.5">
                    {preview.time_range_skipped
                      ? (preview.time_range_skip_reason || 'Not scanned')
                      : formatRecordTimeRange(preview.record_time_first, preview.record_time_last)}
                  </p>
                  {preview.record_time_first && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      Scanned {formatNumber(preview.lines_scanned || 0)} lines across the full file.
                    </p>
                  )}
                </div>
              )}

              {isMeter && (
                <p className="text-xs text-gray-500">
                  Meter files repeat the same EGM every snapshot interval — unique EGM count is from the first ~500 lines read, not only the table below.
                </p>
              )}

              {isPatron && preview.unique_patron_ids_in_sample === 1 && preview.sample_patron_ids?.length === 1 && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  Warning: every sampled line uses patron ID <strong>{preview.sample_patron_ids[0]}</strong> — likely duplicate/test data, not a full member directory.
                </p>
              )}

              {preview.note && (
                <p className="text-xs text-gray-500">{preview.note}</p>
              )}

              {feedId !== 'patron' && preview.venue_check && (
                <>
                  <GamingVenueCheckPanel venueCheck={preview.venue_check} />
                  {preview.sample_venue_names?.length > 0 && (
                    <p className="text-xs text-gray-600">
                      Site names in file: {preview.sample_venue_names.join(', ')}
                      {(preview.unique_venue_names_in_sample || 0) > preview.sample_venue_names.length ? ' …' : ''}
                    </p>
                  )}
                </>
              )}

              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left font-medium text-gray-500 w-8">#</th>
                      <th className="px-2 py-1 text-left font-medium text-gray-500 w-16">{keyColLabel}</th>
                      <th className="px-2 py-1 text-left font-medium text-gray-500 w-28">{secondaryColLabel}</th>
                      {isGamingFeed && (
                        <th className="px-2 py-1 text-left font-medium text-gray-500 w-24">Venue</th>
                      )}
                      <th className="px-2 py-1 text-left font-medium text-gray-500 w-16">Cols</th>
                      <th className="px-2 py-1 text-left font-medium text-gray-500">Line (truncated)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 font-mono">
                    {(preview.sample_lines || []).map(row => (
                      <tr key={row.line_number} className="hover:bg-gray-50">
                        <td className="px-2 py-1 text-gray-400">{row.line_number}</td>
                        <td className="px-2 py-1 text-gray-800">{row.key_id || row.patron_id || row.egm_id || '—'}</td>
                        <td className="px-2 py-1 text-gray-600 truncate max-w-[120px]" title={row.secondary_id}>{row.secondary_id || '—'}</td>
                        {isGamingFeed && (
                          <td className="px-2 py-1 text-gray-600 truncate max-w-[100px]" title={row.venue}>{row.venue || '—'}</td>
                        )}
                        <td className="px-2 py-1 text-gray-600">{row.column_count}</td>
                        <td className="px-2 py-1 text-gray-700 break-all whitespace-pre-wrap">{row.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {isPatron && preview.sample_patron_ids?.length > 0 && (
                <p className="text-xs text-gray-500">
                  Sample patron IDs: {preview.sample_patron_ids.join(', ')}
                  {preview.unique_patron_ids_in_sample > preview.sample_patron_ids.length ? ' …' : ''}
                </p>
              )}

              {(isMeter || isEgm) && preview.sample_egm_ids?.length > 0 && (
                <p className="text-xs text-gray-500">
                  Sample {isEgm ? 'house numbers / EGM PKs' : 'EGM IDs'}: {preview.sample_egm_ids.join(', ')}
                  {preview.unique_egm_ids_in_sample > preview.sample_egm_ids.length ? ' …' : ''}
                </p>
              )}

              {isSessions && preview.sample_egm_ids?.length > 0 && (
                <p className="text-xs text-gray-500">
                  Sample EGM codes: {preview.sample_egm_ids.join(', ')}
                </p>
              )}

              {isSlotAnalysis && preview.sample_egm_ids?.length > 0 && (
                <p className="text-xs text-gray-500">
                  Sample EGM IDs: {preview.sample_egm_ids.join(', ')}
                  {preview.unique_egm_ids_in_sample > preview.sample_egm_ids.length ? ' …' : ''}
                </p>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t flex justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-md text-sm hover:bg-gray-50">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
